import { useState } from 'react';
import { useQuery } from '../hooks/useQuery';
import { useConfig, jiraIssueUrl, hubspotDealUrl } from '../hooks/useConfig';
import { api, type DealLinkImportResult, type DealSplitShare, type OnboardingRecord, type OnboardingOverallStatus, type OnboardingStageStatus } from '../lib/api';
import { EngagementContractPanel } from '../components/EngagementContractPanel';
import { Card, CardHeader, Table, Th, Tr, Td, Badge, ActionButton, Spinner, EmptyState } from '../components/ui';
import { fmtCurrencyWhole } from '../lib/fmt';

const inputStyle: React.CSSProperties = {
  fontSize: 11, padding: '3px 6px', border: '0.5px solid var(--color-border-strong)',
  borderRadius: 'var(--radius-sm)', background: 'var(--color-surface)', color: 'var(--color-text)',
};

const STATUSES = ['new', 'linked', 'in_progress', 'complete', 'dismissed'];

// ─── Historical link backfill ───────────────────────────────────────────────
// Engagements delivered before this tracker existed never got associated, so
// their records carry no account key and the deal↔engagement link was never
// made. Upload a sheet of (deal id, account key) pairs to fill it in.
//
// Lives in the app rather than as a CLI because Cloud SQL isn't reachable from
// a workstation — the script exists but can't be pointed at real data.
//
// Preview is mandatory in practice: the write button doesn't appear until a
// dry run has been read. Same classification runs in both modes, so what the
// preview says is what the write does.
function LinkImportCard({ onImported }: { onImported: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [sheet, setSheet] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<DealLinkImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  async function run(dryRun: boolean) {
    if (!file) return;
    setBusy(true); setError(null);
    try {
      const r = await api.dealOnboarding.importLinks(file, dryRun, sheet.trim() || undefined);
      setResult(r);
      if (!dryRun) onImported();
    } catch (e) {
      setError((e as Error).message);
      setResult(null);
    } finally { setBusy(false); }
  }

  // A capped list — a 400-row sheet shouldn't push the page off screen.
  const list = (label: string, items: string[], tone?: 'warn') => items.length > 0 && (
    <div style={{ marginTop: 6 }}>
      <div style={{ fontSize: 10, color: tone === 'warn' ? 'var(--color-amber-text)' : 'var(--color-text-muted)' }}>
        {label} ({items.length})
      </div>
      <div style={{ fontSize: 10, fontFamily: 'var(--font-mono, monospace)', color: 'var(--color-text-muted)' }}>
        {items.slice(0, 10).join(', ')}{items.length > 10 ? ` …and ${items.length - 10} more` : ''}
      </div>
    </div>
  );

  return (
    <div style={{ padding: '6px 12px', borderBottom: '0.5px solid var(--color-border)' }}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', fontSize: 10, cursor: 'pointer', color: 'var(--color-blue)' }}
      >
        {open ? '▾' : '▸'} Backfill historical deal → account key links
      </button>

      {open && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
          <div style={{ fontSize: 10, color: 'var(--color-text-muted)', maxWidth: 620 }}>
            Spreadsheet with a deal id and an account key per row (a deal name column is
            optional). Fills in the association for engagements that predate this tracker,
            then reconciles the Jira engagement tie. Never overwrites an account key that is
            already set, and never changes a record’s status.
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={(e) => { setFile(e.target.files?.[0] ?? null); setResult(null); setError(null); }}
              style={{ fontSize: 11 }}
            />
            <input
              type="text"
              value={sheet}
              onChange={(e) => setSheet(e.target.value)}
              placeholder="Sheet (optional)"
              style={{ ...inputStyle, width: 140 }}
            />
            <ActionButton onClick={() => run(true)} disabled={!file || busy}>
              {busy ? 'Working…' : 'Preview'}
            </ActionButton>
            {/* Only offered once a preview has been produced for THIS file. */}
            {result?.dry_run && (
              <ActionButton onClick={() => run(false)} disabled={busy} variant="primary">
                Apply {result.inserted + result.account_key_set} change(s)
              </ActionButton>
            )}
          </div>

          {error && <div style={{ fontSize: 11, color: 'var(--color-red-text)' }}>{error}</div>}

          {result && (
            <div style={{ fontSize: 11, background: 'var(--color-bg)', padding: 10, borderRadius: 'var(--radius-sm)' }}>
              <div style={{ fontWeight: 500 }}>
                {result.dry_run ? 'Preview' : 'Imported'} — sheet “{result.sheet}”, {result.parsed} usable row(s)
                {result.skipped_incomplete > 0 && `, ${result.skipped_incomplete} incomplete skipped`}
              </div>
              <div style={{ marginTop: 4, color: 'var(--color-text-muted)' }}>
                {result.inserted} record(s) {result.dry_run ? 'would be' : ''} created ·{' '}
                {result.account_key_set} given an account key ·{' '}
                {result.already_matching} already correct
              </div>
              {!result.dry_run && (
                <div style={{ marginTop: 4, color: 'var(--color-green-text)' }}>
                  {result.tied} engagement tie(s) reconciled · {result.contracts_stamped} contract(s) adopted
                </div>
              )}

              {result.conflicts.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <div style={{ fontSize: 10, color: 'var(--color-amber-text)' }}>
                    {result.conflicts.length} conflict(s) — skipped, existing key left alone
                  </div>
                  {result.conflicts.slice(0, 10).map((c) => (
                    <div key={c.hubspot_deal_id} style={{ fontSize: 10, color: 'var(--color-text-muted)' }}>
                      {c.hubspot_deal_id} {c.deal_name ?? ''} — db <b>{c.db_account_key}</b> vs file <b>{c.file_account_key}</b>
                    </div>
                  ))}
                </div>
              )}
              {list('Deal ids not in HubSpot — these land in the live queue, not filed as historical', result.unknown_deals, 'warn')}
              {list('Account keys matching no Tempo account — will never tie', result.unknown_account_keys, 'warn')}
              {list('Account keys with no engagement — import fine, tie nothing', result.account_keys_without_engagement)}
              {list('Duplicate deal ids in the sheet (first used)', result.duplicate_deal_ids)}

              {result.dry_run && (
                <div style={{ marginTop: 8, fontSize: 10, color: 'var(--color-text-muted)' }}>
                  Nothing written yet.
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// SLA status → badge variant + label + a severity rank (higher = more urgent).
const SLA_VARIANT: Record<OnboardingOverallStatus | OnboardingStageStatus, 'green' | 'blue' | 'gray' | 'amber' | 'red'> = {
  overdue: 'red', due_soon: 'amber', on_track: 'green', complete: 'blue', not_started: 'gray', pending: 'gray',
};
const SLA_LABEL: Record<OnboardingOverallStatus, string> = {
  overdue: 'overdue', due_soon: 'due soon', on_track: 'on track', complete: 'done', not_started: 'not started',
};
const SEVERITY: Record<OnboardingOverallStatus, number> = {
  overdue: 4, due_soon: 3, on_track: 2, not_started: 1, complete: 0,
};

const money = (n: number | null | undefined) => (n != null ? fmtCurrencyWhole(n) : '—');

export default function AdminDealOnboardingPage() {
  const { config } = useConfig();
  // Deep-link support: ?id=<onboardingId> (e.g. from the Slack digest) selects
  // that record on load.
  const [selectedId, setSelectedId] = useState<string | null>(() => {
    try { return new URLSearchParams(window.location.search).get('id'); } catch { return null; }
  });
  const [includeHistorical, setIncludeHistorical] = useState(false);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [atRiskOnly, setAtRiskOnly] = useState(false);
  const [digestMsg, setDigestMsg] = useState<string | null>(null);
  const [digesting, setDigesting] = useState(false);

  async function sendDigest() {
    setDigesting(true);
    setDigestMsg(null);
    try {
      const { result } = await api.dealOnboarding.sendDigest();
      setDigestMsg(result.posted
        ? `Posted to Slack: ${result.overdue} overdue, ${result.due_soon} due soon.`
        : `Not posted — ${result.reason ?? 'nothing to send'}.`);
    } catch (e) {
      setDigestMsg(`Failed: ${(e as Error).message}`);
    } finally {
      setDigesting(false);
    }
  }
  const listQ = useQuery(() => api.dealOnboarding.list({ includeHistorical, includeArchived }), [includeHistorical, includeArchived]);
  const all = listQ.data?.records ?? [];
  const goLive = listQ.data?.go_live_date;

  const overdueCount = all.filter((r) => r.sla.overall === 'overdue').length;
  const dueSoonCount = all.filter((r) => r.sla.overall === 'due_soon').length;

  // Float the most urgent to the top; optional at-risk-only filter.
  const records = [...all]
    .filter((r) => !atRiskOnly || r.sla.overall === 'overdue' || r.sla.overall === 'due_soon')
    .sort((a, b) => SEVERITY[b.sla.overall] - SEVERITY[a.sla.overall]);
  const selected = all.find((r) => r.id === selectedId) ?? null;

  async function refresh() { await listQ.refetch(); }

  async function setStatus(rec: OnboardingRecord, status: string) {
    await api.dealOnboarding.update(rec.id, { status });
    await refresh();
  }

  return (
    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
      {/* LEFT — master list */}
      <div style={{ flex: '0 0 320px', display: 'flex', flexDirection: 'column', gap: 10, minWidth: 0 }}>
        <Card>
          <CardHeader>
            <div style={{ fontSize: 13, fontWeight: 500 }}>Deal onboarding</div>
            <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 10, alignItems: 'center' }}>
              <label style={{ display: 'inline-flex', gap: 5, alignItems: 'center', fontSize: 10, color: 'var(--color-text-muted)' }}>
                <input type="checkbox" checked={includeHistorical} onChange={(e) => setIncludeHistorical(e.target.checked)} />
                Historical
              </label>
              {/* Completed/dismissed records auto-archive out of the default
                  view after a grace period (Settings → auto-archive days). */}
              <label style={{ display: 'inline-flex', gap: 5, alignItems: 'center', fontSize: 10, color: 'var(--color-text-muted)' }}>
                <input type="checkbox" checked={includeArchived} onChange={(e) => setIncludeArchived(e.target.checked)} />
                Archived
              </label>
            </span>
          </CardHeader>
          {/* SLA summary + at-risk filter */}
          <div style={{ padding: '8px 12px', borderBottom: '0.5px solid var(--color-border)', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <Badge variant={overdueCount ? 'red' : 'gray'}>{overdueCount} overdue</Badge>
            <Badge variant={dueSoonCount ? 'amber' : 'gray'}>{dueSoonCount} due soon</Badge>
            <label style={{ marginLeft: 'auto', display: 'inline-flex', gap: 5, alignItems: 'center', fontSize: 10, color: 'var(--color-text-muted)' }}>
              <input type="checkbox" checked={atRiskOnly} onChange={(e) => setAtRiskOnly(e.target.checked)} />
              At risk only
            </label>
          </div>
          <div style={{ padding: '6px 12px', borderBottom: '0.5px solid var(--color-border)', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <ActionButton onClick={sendDigest} disabled={digesting}>{digesting ? 'Sending…' : 'Send Slack digest now'}</ActionButton>
            {digestMsg && <span style={{ fontSize: 10, color: 'var(--color-text-muted)' }}>{digestMsg}</span>}
          </div>
          <LinkImportCard onImported={() => listQ.refetch()} />
          {listQ.isLoading ? <Spinner />
            : records.length === 0 ? <EmptyState message={atRiskOnly ? 'Nothing at risk 🎉' : includeHistorical ? 'No closed-won deals.' : `No deals closed since ${goLive ?? 'the go-live cutoff'}. Tick “Historical” for earlier deals.`} />
            : (
              <div>
                {records.map((r) => {
                  const isSel = r.id === selectedId;
                  const waiting = r.sla.current_stage
                    ? `${r.sla.current_stage}${r.sla.current_waiting_days != null ? ` · ${r.sla.current_waiting_days}d` : ''}`
                    : r.sla.overall === 'complete' ? 'all stages complete' : '—';
                  return (
                    <button
                      key={r.id}
                      onClick={() => setSelectedId(isSel ? null : r.id)}
                      style={{
                        display: 'block', width: '100%', textAlign: 'left', cursor: 'pointer',
                        padding: '8px 12px', border: 'none', borderBottom: '0.5px solid var(--color-border)',
                        background: isSel ? 'var(--color-blue-bg)' : 'transparent', color: 'var(--color-text)',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontSize: 12, fontWeight: 500, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {r.deal_name ?? r.hubspot_deal_id}
                        </span>
                        <Badge variant={SLA_VARIANT[r.sla.overall]}>{SLA_LABEL[r.sla.overall]}</Badge>
                      </div>
                      <div style={{ fontSize: 10, color: 'var(--color-text-muted)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {waiting}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
        </Card>

        <CycleStatsCard />
      </div>

      {/* RIGHT — detail (sticky so it stays in view while the list scrolls) */}
      <div style={{ flex: 1, position: 'sticky', top: 12, minWidth: 0 }}>
        {selected
          ? <DealDetail key={selected.id} rec={selected} config={config} onRefresh={refresh} onSetStatus={setStatus} />
          : <Card><EmptyState message="Select a deal to see its onboarding detail." /></Card>}
      </div>
    </div>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  const empty = value == null || value === '';
  return (
    <div style={{ display: 'flex', gap: 8, fontSize: 12, padding: '2px 0' }}>
      <span style={{ width: 140, flexShrink: 0, color: 'var(--color-text-muted)' }}>{label}</span>
      <span style={{ flex: 1, minWidth: 0 }}>{empty ? '—' : value}</span>
    </div>
  );
}

/** Who shares credit for the deal, from HubSpot. This is what apportions the
 *  deal's sales commission, so it's worth seeing at handoff time — a wrong
 *  split here pays the wrong person for a year.
 *
 *  No split rows is the normal case, not a gap: credit sits wholly with the
 *  deal owner. Percentages that don't total 100 are HubSpot's to fix, so they
 *  are flagged rather than silently normalised. */
function DealSplit({ splits, owner }: { splits: DealSplitShare[]; owner: string | null }) {
  if (splits.length === 0) {
    return <span style={{ color: 'var(--color-text-muted)' }}>none — 100% to {owner ?? 'the deal owner'}</span>;
  }
  const total = splits.reduce((s, r) => s + r.split_percentage, 0);
  const off = Math.abs(total - 1) > 0.001;
  const pct = (n: number) => `${(n * 100).toFixed((n * 100) % 1 === 0 ? 0 : 2)}%`;
  return (
    <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
      {splits.map((s) => (
        <span key={s.hubspot_owner_id}>
          {s.owner_name ?? s.hubspot_owner_id}
          <span style={{ color: 'var(--color-text-muted)' }}> {pct(s.split_percentage)}</span>
        </span>
      ))}
      {off && <Badge variant="amber">totals {pct(total)}</Badge>}
    </span>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--color-text-muted)', padding: '4px 16px', borderTop: '0.5px solid var(--color-border)', borderBottom: '0.5px solid var(--color-border)', background: 'var(--color-surface-sunken, transparent)' }}>
      {children}
    </div>
  );
}

function DealDetail({ rec, config, onRefresh, onSetStatus }: {
  rec: OnboardingRecord;
  config: ReturnType<typeof useConfig>['config'];
  onRefresh: () => Promise<void>;
  onSetStatus: (rec: OnboardingRecord, status: string) => Promise<void>;
}) {
  const [search, setSearch] = useState('');
  const [picking, setPicking] = useState(false);
  const invQ = useQuery(() => api.dealOnboarding.invoices(search), [search, picking]);

  async function pick(invoiceId: string) {
    await api.dealOnboarding.selectInvoice(rec.id, invoiceId);
    setPicking(false);
    await onRefresh();
  }

  async function delink() {
    await api.dealOnboarding.delinkInvoice(rec.id);
    setPicking(false);
    await onRefresh();
  }

  // Seed the picker search with the deal's first word — CO customer names
  // rarely match the full deal name verbatim, so a short prefix gets the best
  // hit rate. The operator can refine it.
  function openPicker() {
    const seed = (rec.deal_name ?? '').trim().split(/\s+/)[0]?.slice(0, 8) ?? '';
    setSearch(seed);
    setPicking(true);
  }

  const engUrl = rec.jira_engagement_key ? jiraIssueUrl(config, rec.jira_engagement_key) : null;
  const dealUrl = hubspotDealUrl(config, rec.hubspot_deal_id);
  const coBase = config?.chargeover_admin_base_url ?? 'https://billing.unlimitedtechsolutions.com';
  const createCustomerUrl = `${coBase}/admin/r/customer/create`;

  return (
    <Card>
      <CardHeader>
        <div style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {dealUrl
            ? <a href={dealUrl} target="_blank" rel="noreferrer">{rec.deal_name ?? rec.hubspot_deal_id} ↗</a>
            : (rec.deal_name ?? rec.hubspot_deal_id)}
        </div>
        <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, alignItems: 'center' }}>
          <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>Status</span>
          <select value={rec.status} onChange={(e) => onSetStatus(rec, e.target.value)} style={inputStyle}>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </span>
      </CardHeader>

      {/* Linked records summary */}
      <div style={{ padding: '10px 16px', display: 'grid', gap: 2 }}>
        <Field label="Account" value={rec.account_name ?? rec.account_key} />
        <Field
          label="Invoice"
          value={rec.chargeover_invoice_id ? (
            // Anchor on status (always populated for synced invoices). The
            // number column is often empty in ChargeOver's bulk list even for
            // issued/paid invoices, so only prefix "#…" when we actually have it
            // rather than blanking the whole field.
            <>{rec.invoice_number ? `#${rec.invoice_number} · ` : ''}
              {rec.invoice_status ?? 'linked'}
              {rec.invoice_total != null ? ` · ${money(rec.invoice_total)}` : ''}
              {rec.invoice_pdf_url && <> · <a href={rec.invoice_pdf_url} target="_blank" rel="noreferrer">PDF ↗</a></>}
            </>
          ) : null}
        />
        <Field
          label="Engagement"
          value={rec.jira_engagement_key
            ? (engUrl ? <a href={engUrl} target="_blank" rel="noreferrer">{rec.jira_engagement_key} ↗</a> : rec.jira_engagement_key)
            : null}
        />
      </div>

      {/* Invoice picker */}
      <div style={{ padding: '0 16px 10px' }}>
        {!picking ? (
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <ActionButton onClick={openPicker}>{rec.chargeover_invoice_id ? 'Change invoice' : 'Link invoice'}</ActionButton>
            {rec.chargeover_invoice_id
              ? <ActionButton onClick={delink}>Delink</ActionButton>
              : <a href={createCustomerUrl} target="_blank" rel="noreferrer" style={{ fontSize: 11 }}>Create customer in ChargeOver ↗</a>}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input placeholder="Search client, account, invoice #…" value={search} onChange={(e) => setSearch(e.target.value)} style={{ ...inputStyle, flex: 1 }} />
              <ActionButton onClick={() => setPicking(false)}>Cancel</ActionButton>
            </div>
            {invQ.isLoading ? <Spinner />
              : (invQ.data?.invoices ?? []).length === 0 ? <EmptyState message="No matching invoices. Try the client name, the Tempo account code, or clear the box to browse recent invoices." />
              : (
                <Table>
                  <thead><tr><Th>Invoice</Th><Th>Client / account</Th><Th>Status</Th><Th>Total</Th><Th>{''}</Th></tr></thead>
                  <tbody>
                    {invQ.data!.invoices.map((inv) => (
                      <Tr key={inv.chargeover_invoice_id}>
                        <Td bold>{inv.invoice_number ? `#${inv.invoice_number}` : inv.chargeover_invoice_id}</Td>
                        <Td muted>
                          {inv.customer_name ?? inv.account_name ?? inv.account_key ?? '—'}
                          {inv.customer_name && (inv.account_name || inv.account_key) ? ` · ${inv.account_name ?? inv.account_key}` : ''}
                        </Td>
                        <Td muted>{inv.raw_status ?? '—'}</Td>
                        <Td>{money(inv.total_amount)}</Td>
                        <Td><ActionButton variant="primary" onClick={() => pick(inv.chargeover_invoice_id)}>Select</ActionButton></Td>
                      </Tr>
                    ))}
                  </tbody>
                </Table>
              )}
          </div>
        )}
      </div>

      {/* SLA timeline */}
      <SectionTitle>
        Onboarding SLA · <Badge variant={SLA_VARIANT[rec.sla.overall]}>{SLA_LABEL[rec.sla.overall]}</Badge>
      </SectionTitle>
      <Table>
        <thead><tr><Th>Stage</Th><Th>Time</Th><Th>Status</Th></tr></thead>
        <tbody>
          {rec.sla.stages.map((s) => (
            <Tr key={s.key}>
              <Td bold>{s.label}</Td>
              <Td muted>
                {s.status === 'complete' ? (s.duration_days != null ? `${s.duration_days}d` : '✓')
                  : s.status === 'pending' ? '—'
                  : `${s.elapsed_days}d${s.due_date ? ` · due ${s.due_date}` : ''}`}
              </Td>
              <Td>
                {s.status === 'pending'
                  ? <span style={{ fontSize: 11, color: 'var(--color-text-hint)' }}>waiting</span>
                  : <Badge variant={SLA_VARIANT[s.status]}>{s.status === 'on_track' ? 'on track' : s.status === 'due_soon' ? 'due soon' : s.status}</Badge>}
              </Td>
            </Tr>
          ))}
        </tbody>
      </Table>

      {/* HubSpot deal */}
      <SectionTitle>HubSpot deal</SectionTitle>
      <div style={{ padding: '8px 16px', display: 'grid', gap: 2 }}>
        <Field label="Owner" value={rec.deal_owner} />
        <Field label="Split" value={<DealSplit splits={rec.deal_splits} owner={rec.deal_owner} />} />
        <Field label="Pipeline" value={rec.pipeline_label} />
        <Field label="Stage" value={rec.stage_label} />
        <Field label="Amount" value={money(rec.deal_amount)} />
        {rec.deal_weighted_amount != null && <Field label="Weighted" value={money(rec.deal_weighted_amount)} />}
        <Field label="Type" value={rec.deal_type} />
        <Field label="Source" value={rec.deal_source} />
        <Field label="Created" value={rec.deal_created_at} />
        <Field label="Closed" value={rec.closed_at} />
        <Field
          label="Handoff doc"
          value={rec.handoff_doc_url ? <a href={rec.handoff_doc_url} target="_blank" rel="noreferrer">open ↗</a> : null}
        />
        <Field
          label="Deal ID"
          value={dealUrl
            ? <a href={dealUrl} target="_blank" rel="noreferrer"><code style={{ fontSize: 11 }}>{rec.hubspot_deal_id}</code> ↗</a>
            : <code style={{ fontSize: 11 }}>{rec.hubspot_deal_id}</code>}
        />
      </div>

      {/* Jira engagement */}
      <SectionTitle>Jira engagement</SectionTitle>
      {rec.jira_engagement_issue_id ? (
        <div style={{ padding: '8px 16px', display: 'grid', gap: 2 }}>
          <Field label="Summary" value={rec.engagement_summary} />
          <Field label="Status" value={rec.engagement_status} />
          <Field label="Type" value={rec.engagement_type} />
          <Field label="Created" value={rec.engagement_created_at} />
          <Field label="SA" value={rec.engagement_sa_name ?? rec.engagement_sa_user_id} />
          <Field label="DM" value={rec.engagement_dm_name ?? rec.engagement_dm_user_id} />
          <Field label="Welcome email" value={rec.welcome_email_sent} />
          <Field label="Pre-kickoff" value={rec.pre_kickoff_date} />
          <Field label="Kickoff" value={rec.kickoff_date} />
          <Field label="Planned ship" value={rec.engagement_planned_ship_date} />
          <Field label="Budget" value={money(rec.engagement_budget)} />
        </div>
      ) : (
        <div style={{ padding: '8px 16px', fontSize: 11, color: 'var(--color-text-muted)' }}>
          Not linked yet — select the deal's invoice to auto-tie its engagement.
        </div>
      )}

      {/* Contract terms — captured here at onboarding, maintained afterwards on
          the engagement budget report. BOTH keys are passed: capture usually
          happens before the Jira engagement is tied, so the record starts
          deal-keyed, and passing both means the lookup still finds it after the
          tie (and a save stamps the engagement onto it). Without that, a
          deal-keyed contract would go invisible the moment the engagement
          linked and someone would capture a duplicate. reconcileEngagementTies
          adopts these in the background too. The timeline chart is hidden —
          there's rarely burn to plot at onboarding. */}
      <SectionTitle>Contract terms</SectionTitle>
      <div style={{ padding: '8px 16px' }}>
        <EngagementContractPanel
          engagementId={rec.jira_engagement_issue_id}
          dealId={rec.hubspot_deal_id}
          showTimeline={false}
        />
      </div>

      {/* Checklist */}
      <SectionTitle>Onboarding checklist ({rec.checklist_done}/{rec.checklist_total})</SectionTitle>
      <Table>
        <thead><tr><Th>Step</Th><Th>Detail</Th><Th>Status</Th></tr></thead>
        <tbody>
          {rec.checklist.map((s) => (
            <Tr key={s.key}>
              <Td bold>{s.label}</Td>
              <Td muted>{s.detail ?? '—'}</Td>
              <Td>{s.done ? <Badge variant="green">done</Badge> : <Badge variant="gray">open</Badge>}</Td>
            </Tr>
          ))}
        </tbody>
      </Table>
    </Card>
  );
}

/** Cycle-time measurement: median / p90 per transition over completed stages. */
function CycleStatsCard() {
  const [open, setOpen] = useState(false);
  const statsQ = useQuery(() => (open ? api.dealOnboarding.stats() : Promise.resolve({ stats: [] })), [open]);
  const stats = statsQ.data?.stats ?? [];
  return (
    <Card>
      <CardHeader>
        <div style={{ fontSize: 13, fontWeight: 500 }}>Cycle times</div>
        <button onClick={() => setOpen((o) => !o)} style={{ marginLeft: 'auto', ...inputStyle, cursor: 'pointer' }}>
          {open ? 'Hide' : 'Show'}
        </button>
      </CardHeader>
      {open && (
        statsQ.isLoading ? <Spinner />
          : (
            <Table>
              <thead><tr><Th>Transition</Th><Th>n</Th><Th>median</Th><Th>p90</Th></tr></thead>
              <tbody>
                {stats.map((s) => (
                  <Tr key={s.key}>
                    <Td bold>{s.label}</Td>
                    <Td muted>{s.count}</Td>
                    <Td>{s.median != null ? `${s.median}d` : '—'}</Td>
                    <Td>{s.p90 != null ? `${s.p90}d` : '—'}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          )
      )}
    </Card>
  );
}
